import { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Heart, Gift, Users, Mail, ExternalLink, HandHeart } from 'lucide-react';
import { Layout } from '../components/layout/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Breadcrumbs } from '../components/layout/Breadcrumbs';
import { REGISTERED_CHARITY_LABEL } from '../constants/charity';
import { PHONE_NUMBER, PHONE_TEL_HREF } from '../constants/contact';
import { scheduleScrollToElementById } from '../constants/scrollNavigation';

const DONATE_HASH_SCROLL_IDS = ['donate-main', 'legacy', 'membership', 'partners'];

const DonatePage = () => {
  const location = useLocation();

  useEffect(() => {
    const scrollId = location.hash.slice(1);
    if (!DONATE_HASH_SCROLL_IDS.includes(scrollId)) return;
    return scheduleScrollToElementById(scrollId, { delayMs: 50 });
  }, [location.pathname, location.hash]);

  return (
    <Layout>
      <div data-testid="donate-page">
        {/* Hero */}
        <section className="relative py-24 md:py-32 overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://images.unsplash.com/photo-1542601906990-b4d3fb778b09?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920"
              alt="Hands holding nature"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-cpre-autumn-leaf/85" />
          </div>
          <div className="relative container-custom">
            <Breadcrumbs
              variant="light"
              customItems={[{ label: 'Get Involved', href: '/volunteering' }, { label: 'Make a Donation' }]}
            />
            <div className="text-center text-white mt-8">
              <Badge className="bg-cpre-harvest-gold text-cpre-new-yew hover:bg-cpre-autumn-leaf hover:text-white mb-6 inline-flex items-center gap-2 rounded-full text-sm font-medium px-4 py-2">
                <Heart className="w-4 h-4" />
                Support Our Work
              </Badge>
              <h1 className="text-4xl sm:text-5xl font-heading font-bold mb-6 text-white">Make a Donation</h1>
              <p className="text-xl text-white/90 max-w-2xl mx-auto">
                Help us enhance, promote and protect the beautiful countryside of Rutland and Lincolnshire.
              </p>
            </div>
          </div>
        </section>

        {/* Main Content */}
        <section id="donate-main" className="section-padding">
          <div className="container-custom">
            <div className="max-w-3xl mx-auto">
              {/* Key Message Card */}
              <Card className="bg-white border-stone-200 mb-12">
                <CardContent className="p-8 md:p-12">
                  <div className="flex flex-col md:flex-row gap-8 items-start">
                    <div className="w-32 h-32 md:w-40 md:h-40 rounded-2xl overflow-hidden flex-shrink-0">
                      <img 
                        src="https://images.unsplash.com/photo-1542601906990-b4d3fb778b09?crop=entropy&cs=srgb&fm=jpg&q=85&w=400"
                        alt="Hands holding nature"
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div>
                      <h2 className="text-2xl md:text-3xl font-heading font-bold mb-4">
                        Make a donation to CPRE Rutland & Lincolnshire
                      </h2>
                      <p className="text-muted-foreground leading-relaxed mb-4">
                        If you would like to do even more for the Rutland and Lincolnshire countryside, want to remember 
                        someone with a gift in their name, or want to make a one-time gift to help protect your local 
                        countryside, CPRE Rutland & Lincolnshire always welcomes donations.
                      </p>
                      <p className="text-muted-foreground leading-relaxed mb-6">
                        We are a registered charity ({REGISTERED_CHARITY_LABEL}), run exclusively by volunteers, and we
                        rely entirely on the kindness and generosity of like-minded people like you to support our work.
                      </p>
                      <div className="flex items-center gap-2 text-cpre-new-yew">
                        <Mail className="w-5 h-5" />
                        <span>For more information: </span>
                        <a href="mailto:chair@cprerutlandandlincs.uk" className="font-medium hover:underline">
                          chair@cprerutlandandlincs.uk
                        </a>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Stand Up For Countryside Section */}
              <div className="bg-cpre-new-yew rounded-2xl p-8 md:p-12 text-white text-center mb-12">
                <HandHeart className="w-16 h-16 mx-auto mb-6 text-cpre-mint" />
                <h2 className="text-2xl md:text-3xl font-heading font-bold mb-4 text-white">
                  Stand up for the countryside
                </h2>
                <p className="text-white/90 leading-relaxed max-w-2xl mx-auto mb-8">
                  Joining or donating to CPRE is the best way to help enhance, protect and promote the countryside 
                  we love. Working together, we can make the countryside a better place for everyone to live, work and enjoy.
                </p>
                <a 
                  href="https://donate.cpre.org.uk/page/178371/donate/1?ea.tracking.id=branch-rutlincs-web" 
                  target="_blank" 
                  rel="noopener noreferrer"
                >
                  <Button size="lg" className="bg-cpre-autumn-leaf hover:bg-cpre-autumn-leaf/90 text-white rounded-full px-10 h-14 text-lg" data-testid="donate-external-btn">
                    <Heart className="w-5 h-5 mr-2" />
                    Be part of it
                    <ExternalLink className="w-4 h-4 ml-2" />
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Other Ways to Give */}
        <section className="section-padding bg-cpre-pebble/30">
          <div className="container-custom text-center">
            <h2 className="text-2xl font-heading font-bold mb-4">Other Ways to Support</h2>
            <p className="text-muted-foreground mb-8 max-w-2xl mx-auto">
              There are many ways you can help protect the countryside of Rutland and Lincolnshire.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
              <Card id="legacy" className="bg-white border-stone-200 hover:shadow-lg transition-shadow duration-300">
                <CardContent className="p-6 text-center">
                  <Gift className="w-10 h-10 text-cpre-autumn-leaf mx-auto mb-4" />
                  <h4 className="font-semibold mb-2">Leave a Legacy</h4>
                  <p className="text-sm text-muted-foreground mb-4">
                    Remember someone special or leave a lasting legacy by including CPRE in your will.
                  </p>
                  <a href="mailto:chair@cprerutlandandlincs.uk">
                    <Button variant="outline" className="border-cpre-new-yew text-cpre-new-yew hover:bg-cpre-new-yew hover:text-white rounded-full">
                      Contact Us
                    </Button>
                  </a>
                </CardContent>
              </Card>

              <Card id="membership" className="bg-white border-stone-200 hover:shadow-lg transition-shadow duration-300">
                <CardContent className="p-6 text-center">
                  <Users className="w-10 h-10 text-cpre-new-yew mx-auto mb-4" />
                  <h4 className="font-semibold mb-2">Become a Member</h4>
                  <p className="text-sm text-muted-foreground mb-4">
                    Join CPRE and support our work with an annual membership.
                  </p>
                  <a href="https://www.cpre.org.uk/join-us/" target="_blank" rel="noopener noreferrer">
                    <Button variant="outline" className="border-cpre-new-yew text-cpre-new-yew hover:bg-cpre-new-yew hover:text-white rounded-full">
                      Join CPRE
                      <ExternalLink className="w-3 h-3 ml-2" />
                    </Button>
                  </a>
                </CardContent>
              </Card>

              <Card id="partners" className="bg-white border-stone-200 hover:shadow-lg transition-shadow duration-300">
                <CardContent className="p-6 text-center">
                  <Heart className="w-10 h-10 text-cpre-harvest-gold mx-auto mb-4" />
                  <h4 className="font-semibold mb-2">Corporate Partners</h4>
                  <p className="text-sm text-muted-foreground mb-4">
                    Partner with CPRE as part of your corporate social responsibility program.
                  </p>
                  <a href="mailto:chair@cprerutlandandlincs.uk">
                    <Button variant="outline" className="border-cpre-new-yew text-cpre-new-yew hover:bg-cpre-new-yew hover:text-white rounded-full">
                      Contact Us
                    </Button>
                  </a>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section className="py-12 bg-white border-t border-stone-200">
          <div className="container-custom text-center">
            <p className="text-muted-foreground mb-2">{REGISTERED_CHARITY_LABEL}</p>
            <p className="text-muted-foreground">
              Questions about donating? Contact us at{' '}
              <a href="mailto:chair@cprerutlandandlincs.uk" className="text-cpre-new-yew font-medium hover:underline">
                chair@cprerutlandandlincs.uk
              </a>
              {' '}or call{' '}
              <a href={PHONE_TEL_HREF} className="text-cpre-new-yew font-medium hover:underline">
                {PHONE_NUMBER}
              </a>
            </p>
          </div>
        </section>
      </div>
    </Layout>
  );
};

export default DonatePage;
